import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LogOut,
  Menu,
  X,
  ChevronRight,
  Moon,
  Sun,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { Button } from '../ui/button';
import { toast } from '../../hooks/use-toast';
import { getAdminMenuItems } from './adminMenuItems';

const AdminSidebar = ({ currentSection, onSectionChange, isSidebarOpen = false, onToggleSidebar }) => {
  const [isCollapsed, setIsCollapsed] = useState(false); // For desktop collapse
  const [pendingTrainersCount, setPendingTrainersCount] = useState(0);
  const [pendingReviewsCount, setPendingReviewsCount] = useState(0);
  const [unreadTicketsCount, setUnreadTicketsCount] = useState(0);
  const [reportedMessagesCount, setReportedMessagesCount] = useState(0);
  const [reportedTrainersCount, setReportedTrainersCount] = useState(0);
  const [criticalTrainersCount, setCriticalTrainersCount] = useState(0);
  const [lastViewedWarningsCount, setLastViewedWarningsCount] = useState(0);
  const [advertisingInquiriesCount, setAdvertisingInquiriesCount] = useState(0);
  const { logout, user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  // Load last viewed warnings count from localStorage
  useEffect(() => {
    const savedCount = localStorage.getItem('lastViewedWarningsCount');
    if (savedCount) {
      setLastViewedWarningsCount(parseInt(savedCount, 10));
    }
  }, []);

  // Handle navigation with badge management
  const handleNavigation = async (itemId) => {
    // If clicking on trainer-warnings, mark current count as viewed
    if (itemId === 'trainer-warnings') {
      localStorage.setItem('lastViewedWarningsCount', criticalTrainersCount.toString());
      setLastViewedWarningsCount(criticalTrainersCount);
    }
    
    // If clicking on advertising-inquiries, mark all as viewed via API
    if (itemId === 'advertising-inquiries') {
      try {
        const token = localStorage.getItem('token');
        await fetch(`${API_BASE}/api/admin/advertising/inquiries/mark-viewed`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setAdvertisingInquiriesCount(0);
      } catch (error) {
        console.error('Error marking inquiries as viewed:', error);
      }
    }
    
    onSectionChange(itemId);
  };

  const fetchPendingCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch pending trainers
      const trainersResponse = await fetch(`${API_BASE}/api/admin/trainers/pending`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (trainersResponse.ok) {
        const trainersData = await trainersResponse.json();
        setPendingTrainersCount(trainersData.length);
      }

      // Fetch pending reviews
      const reviewsResponse = await fetch(`${API_BASE}/api/admin/reviews`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (reviewsResponse.ok) {
        const reviewsData = await reviewsResponse.json();
        const pendingCount = (reviewsData.reviews || []).filter(r => r.status === 'pending').length;
        setPendingReviewsCount(pendingCount);
      }

      // Fetch unread support tickets (open status)
      const ticketsResponse = await fetch(`${API_BASE}/api/admin/support/tickets?status=open`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (ticketsResponse.ok) {
        const ticketsData = await ticketsResponse.json();
        setUnreadTicketsCount(ticketsData.tickets?.length || 0);
      }

      // Fetch reported messages (pending status)
      const reportedMsgResponse = await fetch(`${API_BASE}/api/admin/messages/reports?status=pending`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (reportedMsgResponse.ok) {
        const reportedMsgData = await reportedMsgResponse.json();
        setReportedMessagesCount(reportedMsgData.reports?.length || 0);
      }

      // Fetch reported trainers (pending status)
      const reportedTrainersResponse = await fetch(`${API_BASE}/api/admin/trainers/reports?status=pending`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (reportedTrainersResponse.ok) {
        const reportedTrainersData = await reportedTrainersResponse.json();
        setReportedTrainersCount(reportedTrainersData.reports?.length || 0);
      }

      // Fetch critical trainers count (5+ reports)
      const criticalTrainersResponse = await fetch(`${API_BASE}/api/admin/critical-trainers-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (criticalTrainersResponse.ok) {
        const criticalData = await criticalTrainersResponse.json();
        setCriticalTrainersCount(criticalData.critical_count || 0);
      }

      // Fetch new advertising inquiries count
      const advertisingResponse = await fetch(`${API_BASE}/api/admin/advertising/inquiries/count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (advertisingResponse.ok) {
        const advertisingData = await advertisingResponse.json();
        setAdvertisingInquiriesCount(advertisingData.new_count || 0);
      }
    } catch (error) {
      console.error('Error fetching pending counts:', error);
    }
  }, [API_BASE]);
  useEffect(() => {
    fetchPendingCount();
    // Refresh every 10 seconds (instant updates)
    const interval = setInterval(fetchPendingCount, 10000);
    return () => clearInterval(interval);
  }, [fetchPendingCount]);
  
  // Refresh when current section changes
  useEffect(() => {
    fetchPendingCount();
  }, [currentSection, fetchPendingCount]);

  const menuItems = getAdminMenuItems({
    pendingTrainersCount,
    reportedMessagesCount,
    reportedTrainersCount,
    criticalTrainersCount,
    lastViewedWarningsCount,
    unreadTicketsCount,
    advertisingInquiriesCount,
    pendingReviewsCount,
  });


  const handleLogout = () => {
    logout();
    toast({ title: 'Logged out successfully' });
    navigate('/login');
  };

  return (
    <>
      {/* Desktop Sidebar */}
      <div 
        className={`hidden lg:flex lg:flex-col bg-gradient-to-b from-gray-800 to-gray-900 dark:from-gray-900 dark:to-black text-white h-screen sticky top-0 transition-all duration-300 z-50 ${
          isCollapsed ? 'w-20' : 'w-64'
        }`}
      >
        {/* Header */}
        <div className="p-4 border-b border-gray-700 flex items-center justify-between flex-shrink-0">
          {!isCollapsed && (
            <div>
              <h2 className="font-bold text-lg">Admin Panel</h2>
              <p className="text-xs text-gray-300">{user?.name || 'Admin'}</p>
            </div>
          )}
          <div className="flex items-center gap-1">
            {/* Theme Toggle Button */}
            <button
              onClick={toggleTheme}
              className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
              title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {theme === 'dark' ? (
                <Sun className="w-5 h-5 text-yellow-400" />
              ) : (
                <Moon className="w-5 h-5 text-gray-300" />
              )}
            </button>
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
            >
              {isCollapsed ? <Menu className="w-5 h-5" /> : <X className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-2 overflow-y-auto flex-1 min-h-0">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = currentSection === item.id;
            
            return (
              <button
                key={item.id}
                onClick={() => handleNavigation(item.id)}
                className={`w-full flex items-center gap-3 p-3 rounded-lg mb-1 transition-all ${
                  isActive 
                    ? 'bg-red-600 text-white shadow-lg' 
                    : 'hover:bg-gray-700 text-white'
                }`}
                title={isCollapsed ? item.label : ''}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                
                {!isCollapsed && (
                  <>
                    <div className="flex-1 text-left">
                      <div className="font-medium text-sm">{item.label}</div>
                      <div className={`text-xs ${isActive ? 'text-red-900' : 'text-gray-300'}`}>
                        {item.description}
                      </div>
                    </div>
                    
                    {item.badge > 0 && (
                      <span className={`text-white text-xs rounded-full px-2 py-0.5 ${
                        item.badgeVariant === 'critical' ? 'bg-red-600 animate-pulse' : 'bg-red-500'
                      }`}>
                        {item.badge}
                      </span>
                    )}
                    
                    {isActive && <ChevronRight className="w-4 h-4" />}
                  </>
                )}
              </button>
            );
          })}
        </nav>

        {/* Logout */}
        <div className="p-4 border-t border-gray-700 flex-shrink-0">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 p-3 hover:bg-gray-700 rounded-lg transition-colors"
          >
            <LogOut className="w-5 h-5" />
            {!isCollapsed && <span>Logout</span>}
          </button>
        </div>
      </div>

      {/* Mobile Sidebar */}
      <div 
        className={`lg:hidden fixed inset-y-0 left-0 flex flex-col bg-gradient-to-b from-gray-800 to-gray-900 dark:from-gray-900 dark:to-black text-white w-64 transition-transform duration-300 z-50 ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="p-4 border-b border-gray-700 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="font-bold text-lg">Admin Panel</h2>
            <p className="text-xs text-gray-300">{user?.name || 'Admin'}</p>
          </div>
          <div className="flex items-center gap-1">
            {/* Theme Toggle - Mobile */}
            <button
              onClick={toggleTheme}
              className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
              title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {theme === 'dark' ? (
                <Sun className="w-5 h-5 text-yellow-400" />
              ) : (
                <Moon className="w-5 h-5 text-gray-300" />
              )}
            </button>
            <button
              onClick={onToggleSidebar}
              className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

      {/* Navigation - Mobile */}
      <nav className="p-2 overflow-y-auto flex-1 min-h-0">
        {menuItems.map((item) => {
          const Icon = item.icon;
          const isActive = currentSection === item.id;
          
          return (
            <button
              key={item.id}
              onClick={() => handleNavigation(item.id)}
              className={`w-full flex items-center gap-3 p-3 rounded-lg mb-1 transition-all active:scale-95 ${
                isActive 
                  ? 'bg-red-600 text-white shadow-lg' 
                  : 'hover:bg-gray-700 text-white active:bg-red-700'
              }`}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              
              <div className="flex-1 text-left">
                <div className="font-medium text-sm">{item.label}</div>
                <div className={`text-xs ${isActive ? 'text-red-900' : 'text-gray-300'}`}>
                  {item.description}
                </div>
              </div>
              
              {item.badge > 0 && (
                <span className={`text-white text-xs rounded-full px-2 py-0.5 font-semibold ${
                  item.badgeVariant === 'critical' ? 'bg-red-600 animate-pulse' : 'bg-red-500'
                }`}>
                  {item.badge}
                </span>
              )}
              
              {isActive && <ChevronRight className="w-4 h-4" />}
            </button>
          );
        })}
      </nav>

      {/* Logout - Mobile */}
      <div className="p-4 border-t border-gray-700 flex-shrink-0">
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 p-3 hover:bg-gray-700 rounded-lg transition-colors active:scale-95"
        >
          <LogOut className="w-5 h-5" />
          <span>Logout</span>
        </button>
      </div>
      </div>
    </>
  );
};

export default AdminSidebar;
